import {
  Children,
  cloneElement,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { animated, useSpring, useSpringRef } from 'react-spring';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconHeroSlider } from '@hivebrite/design-system/Icon';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import { useSectionContext } from 'hb-builder/contexts/SectionContext';
import {
  useBuilderContext,
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';

import Pagination from './components/Pagination';
import Slide from './components/Slide';
import Settings from './Settings';

const SWITCH_INTERVAL = 5000;

const HeroSlider: BuilderComponent<HeroItemAttributes> = () => <div />; // never used

const useReorderedObserver = (slides) => {
  const [slidesList, setSlidesList] = useState(() =>
    slides.map((slide) => slide.id),
  );
  const [isSlidesReordered, setIsSlidesReordered] = useState(false);

  const doesOrderChange = useCallback(() => {
    const currenSlidesWithPosition = slides.map((slide) => slide.id);

    if (slidesList.toString() !== currenSlidesWithPosition.toString()) {
      setSlidesList(currenSlidesWithPosition);
      setIsSlidesReordered((prev) => !prev);
    }
  }, [slides, slidesList]);

  useEffect(() => {
    doesOrderChange();
  }, [doesOrderChange]);

  return isSlidesReordered;
};

const RenderBuilder = ({ children }) => {
  const { updateAttribute } = useBuilderContext();
  const { item } = useComponentContext();
  const { components } = useComponentsContext();
  const isSlidesReordered = useReorderedObserver(item.children);

  const { attributes } = item;
  const { Section } = components;

  const cancelFocusedSlide = () => {
    updateAttribute({
      id: item.id,
      attribute: '__lastItemModified',
      value: null,
    });
  };

  return (
    <Section.RenderBuilder attributes={attributes}>
      <BuilderThemeProvider viewAs={VIEW_AS.USER}>
        <HeroSliderContainer
          cancelFocusedSlide={cancelFocusedSlide}
          isSlidesReordered={isSlidesReordered}
          item={item as DocumentItem<HeroItemAttributes>}
          height={item.attributes?.height}
        >
          {children}
        </HeroSliderContainer>
      </BuilderThemeProvider>
    </Section.RenderBuilder>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<HeroItemAttributes>>) => {
  const { bodyWidth, isMobilePreview, workspaceConfig } = useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      components={components}
      isMobilePreview={isMobilePreview}
      item={item}
      workspaceConfig={workspaceConfig}
    >
      <HeroSliderContainer
        item={item!}
        height={item!.attributes?.height?.toString()}
        withInterval
      >
        {children}
      </HeroSliderContainer>
    </Section.Render>
  );
};

interface HeroSliderContainerProps {
  cancelFocusedSlide?: () => void;
  children: ReactElement[] | ReactElement;
  isSlidesReordered?: boolean;
  item: DocumentItem<HeroItemAttributes>;
  height?: string;
  withInterval?: boolean;
}

const HeroSliderContainer = ({
  cancelFocusedSlide,
  children,
  isSlidesReordered,
  item,
  height,
  withInterval,
}: HeroSliderContainerProps) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [intervalActivated, setIntervalActivated] = useState(withInterval);

  const { sectionWidth } = useSectionContext();
  const transRef = useSpringRef();
  const handlers = useSwipeable({
    onSwiped: (eventData) => switchSlide(eventData.dir.toLowerCase()),
  });

  const numberOfSlide = item.children?.length || 0;
  const multiplesSlides = numberOfSlide > 1;

  const translateX = useCallback(
    () => (sectionWidth ? -(sectionWidth * activeSlideIndex) : 0),
    [sectionWidth, activeSlideIndex],
  );

  const transitionX = useSpring({ x: translateX() });

  const handleDotOnClick = (slideIndex) => {
    cancelFocusedSlide?.();
    setActiveSlideIndex(slideIndex);
  };

  const isLastSlide = (slideIndex) =>
    item.children && slideIndex === item.children.length - 1;

  const switchSlide = (direction = 'left') => {
    setActiveSlideIndex((currentIndex) => {
      if (direction === 'right' && currentIndex === 0) {
        return numberOfSlide - 1;
      }
      if (direction === 'right') {
        return currentIndex - 1;
      }
      if (direction === 'left' && isLastSlide(currentIndex)) {
        return 0;
      }

      return currentIndex + 1;
    });
  };

  useEffect(() => {
    const focusedSlideIndex = item.children?.findIndex(
      (slide) => slide.id === item.attributes?.__lastItemModified,
    );

    if (
      focusedSlideIndex &&
      focusedSlideIndex !== -1 &&
      focusedSlideIndex !== activeSlideIndex
    ) {
      setActiveSlideIndex(focusedSlideIndex);
    }
  }, [activeSlideIndex, isSlidesReordered, item]);

  useEffect(() => {
    if (withInterval) {
      transRef.start();

      const clock = setTimeout(
        () => (intervalActivated ? switchSlide() : null),
        SWITCH_INTERVAL,
      );

      return () => {
        clearTimeout(clock);
      };
    }
  }, [activeSlideIndex, intervalActivated, withInterval]);

  const childrenWithRef = Children.map(children, (child, childIndex) =>
    cloneElement(child, {
      isActive: activeSlideIndex === childIndex,
    }),
  );

  return (
    <StyledHeroSliderContainer
      dataTestId="builder-hero-slider-container"
      {...handlers}
      $cornerRadius={
        !item.attributes?.outsideGrid && item.attributes?.cornerRadius
      }
      $height={height}
      {...(withInterval && {
        onMouseEnter: () => {
          setIntervalActivated(false);
        },
        onMouseLeave: () => {
          setIntervalActivated(true);
        },
      })}
    >
      {!!sectionWidth && (
        <>
          <animated.div
            data-testid="builder-hero-slider-slides"
            style={{
              display: 'flex',
              height: 'auto',
              transform: transitionX.x.to((x) => `translate3d(${x}px, 0, 0)`),
              width: sectionWidth * (item.children?.length || 0),
            }}
          >
            {childrenWithRef}
          </animated.div>
          {multiplesSlides && (
            <Pagination
              numberOfElements={numberOfSlide}
              activeElement={activeSlideIndex}
              onClickCallback={handleDotOnClick}
            />
          )}
        </>
      )}
    </StyledHeroSliderContainer>
  );
};

HeroSlider.componentFamily = 'Hero';
HeroSlider.componentName = 'HeroSlider';
HeroSlider.componentType = 'section';
HeroSlider.componentLabel = __('Hero slider');
HeroSlider.componentCategory = 'block';
HeroSlider.componentIcon = () => IconHeroSlider;

HeroSlider.canBeDoubleClicked = true;
HeroSlider.canBeDragged = true;
HeroSlider.canBeListed = true;
HeroSlider.canBeSelected = true;

const instantiatesSlides = ({
  bodyWidth,
  components,
  featureFlags,
  features,
  slideNumber,
  theme,
}) =>
  Array(slideNumber)
    .fill(null)
    .map((_, index) =>
      Slide.item({
        bodyWidth,
        components,
        featureFlags,
        features,
        theme,
        index,
      }),
    );

const defaultConfig = ({
  bodyWidth,
  components,
  featureFlags,
  features,
  theme,
}) => {
  const slides = instantiatesSlides({
    bodyWidth,
    components,
    featureFlags,
    features,
    slideNumber: 3,
    theme,
  });

  return {
    children: slides,
    sectionWidth: bodyWidth,
  };
};

HeroSlider.item = ({
  bodyWidth,
  components = {},
  features = {},
  featureFlags,
  theme,
}) => {
  const heroSliderConfig = {
    ...defaultConfig({ bodyWidth, components, featureFlags, features, theme }),
    ...(features?.heroSlider || {}),
  };

  const { children, outsideGrid, height, sectionWidth } = heroSliderConfig;
  const { Section } = components;

  return Section.item({
    children,
    components,
    features: {
      ...features,
      section: {
        ...features.section,
        height,
        name: HeroSlider.componentName,
        outsideGrid,
        sectionWidth,
        type: HeroSlider.componentName,
      },
    },
  });
};

HeroSlider.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

HeroSlider.Settings = Settings;
HeroSlider.SettingsTabs = defaultTabs;

HeroSlider.RenderBuilder = RenderBuilder;
HeroSlider.Render = Render;

export default HeroSlider;

interface StyledHeroSliderContainerProps {
  $cornerRadius?: number;
  $height?: string;
}

const StyledHeroSliderContainer = styled(Box).attrs(
  ({ $height }: StyledHeroSliderContainerProps) => ({
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    minHeight: `${$height}px`,
    width: '100%',
  }),
)<StyledHeroSliderContainerProps>`
  overflow: hidden;
  position: relative;
  border-radius: ${({ $cornerRadius }) =>
    $cornerRadius ? `${$cornerRadius}px` : 0};
`;
